import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { businessDate, money } from '../../lib/format';
import { useShopLogo } from '../../lib/images';
import { useShop } from '../../shop/ShopProvider';
import { receiptData, shopHeader } from '../sales/print/builders';
import { Receipt80 } from '../sales/print/documents';
import { useEmail } from '../email/api';
import { EmailSend } from '../email/EmailSend';
import { useWhatsapp, WhatsappSend, type WhatsappTarget } from '../whatsapp';
import type { FoodpandaOrder } from './api';

/**
 * WhatsApp and Email tie-in: send the Foodpanda customer the receipt (80 mm PDF of the invoice)
 * when the shop also has WhatsApp or Email. Foodpanda often masks the customer's number; the dialog lets staff type it.
 */
export function FoodpandaWhatsapp({ order }: { order: FoodpandaOrder }) {
  const { can, me } = useAuth();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const wa = useWhatsapp();
  const email = useEmail();
  const invoiceId = order.invoice?.id;
  const enabled =
    !!invoiceId &&
    (wa.canSend || (email.canSend && !!email.sender('sales_invoice'))) &&
    can('sales.view');
  const invoice = useQuery({
    queryKey: ['sales-invoices', 'detail', invoiceId],
    queryFn: () =>
      unwrap(api.GET('/sales-invoices/{id}', { params: { path: { id: invoiceId ?? '' } } })),
    enabled,
  });
  const inv = invoice.data;
  if (!enabled || !inv) return null;
  const shop = shopHeader(settings, undefined, logo);
  const receipt = () => (
    <Receipt80
      shop={shop}
      data={receiptData({ invoice: inv, payment: null, changeDue: '0' }, format, {
        cashier: me?.user.name,
      })}
    />
  );
  const target: WhatsappTarget = {
    documentType: 'sales_invoice',
    documentId: inv.id,
    documentRef: inv.number,
    contact: {
      id: inv.contact.id,
      name: order.customerName ?? 'Customer',
      phone: order.customerPhone,
    },
    vars: {
      number: inv.number,
      name: order.customerName ?? undefined,
      amount: money(order.total, format),
      date: businessDate(inv.docDate, format),
    },
    pdf: { fileName: `${inv.number}.pdf`, paper: '80mm', render: receipt },
  };
  return (
    <>
      <WhatsappSend label="Receipt on WhatsApp" target={target} />
      <EmailSend label="Receipt by email" target={target} />
    </>
  );
}
